import { Pagination } from "../types/pagination";
import { ISoleneService } from "../types/services/solene-sevice";
import {
  SoleneClubVisitDetails,
  SoleneClubVisitFormPayload,
  SoleneClubVisitListingRow,
  SoleneClubVisitLookups,
  SoleneClubVisitMember,
  SoleneDayPassDetails,
  SoleneDayPassFormPayload,
  SoleneDayPassLineItem,
  SoleneDayPassLineItemPayload,
  SoleneDayPassTransactionDetails,
  SoleneDocument,
  SoleneLohonoCheckResult,
  SoleneMemberItem,
  SoleneOpportunity,
  SoleneOpportunityItem,
  SoleneOpportunityMemberProfile,
  SoleneOpportunityMembersData,
  SoleneOpportunityMembership,
  SoleneOpportunityPayment,
  SolenePayment,
  SolenePaymentApprovalItem,
  SolenePaymentLink,
} from "../types/solene";
import { DELETE, FetchResult, GET, PATCH, POST } from "../utils/fetch";

class SoleneService implements ISoleneService {
  async addOpportunity(
    first_name: string,
    last_name: string,
    email: string,
    phone_no: string,
    source_of_enquiry: string,
    message: string,
    source_of_enquiry_other?: string,
    membership_poc?: number,
    sales_poc?: number,
    agent?: number,
  ): Promise<FetchResult<string>> {
    const [countryCode, phoneNo] = phone_no.split(" ");

    const payload: any = {
      email: email || "",
      country_code: countryCode || "",
      mobile: phoneNo || "",
      source: source_of_enquiry,
      source_others: source_of_enquiry_other || "",
      membership_exec_id: membership_poc,
      message: message || "",
    };

    let name = first_name;

    if (last_name) {
      name = `${name} ${last_name}`;
    }

    payload.name = name;

    if (sales_poc) {
      payload.poc_exec_id = sales_poc;
    }

    if (source_of_enquiry === "agent" && agent !== undefined) {
      payload.agent_id = agent;
    }

    const url = `/api/v2/solene/opportunities`,
      { response, error } = await POST<OpportunityAddResponse>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        paginate: undefined,
        response: null,
      };
    }

    return {
      error: null,
      response: response.slug,
    };
  }

  async getSoleneOpportunities(
    phone_no?: string,
    statuses?: string[],
    stages?: string[],
    sources?: string[],
    enquired_from?: string,
    enquired_until?: string,
    staff?: string,
    search?: string,
    page?: string,
    page_size?: string,
  ): Promise<FetchResult<SoleneOpportunityItem[]>> {
    const qp = getFilterQp(
      undefined,
      phone_no,
      statuses,
      stages,
      sources,
      enquired_from,
      enquired_until,
      undefined,
      undefined,
      staff,
      undefined,
      search,
      page,
      page_size,
    );

    const marshalled = qp.toString();

    let url = "/api/v2/solene/opportunities";

    if (marshalled) {
      url = `${url}?${marshalled}`;
    }

    const { error, response } = await GET<OpportunitiesListResponse>(url);

    if (error) {
      return {
        error,
        paginate: undefined,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        paginate: undefined,
        response: null,
      };
    }

    const formatted = response.opportunities.map((each) => {
      const {
          name,
          email,
          mobile,
          check_in,
          check_out,
          enquired_at,
          existing_solene_user,
          updated_at,
          location,
          poc_exec,
          slug,
          status,
          verified,
          repeat_user,
          source,
          country_code,
          referred_by_tier_name,
          properties,
        } = each,
        phoneNo = `${country_code || ""} ${mobile || ""}`.trim(),
        opportunity: SoleneOpportunityItem = {
          slug,
          name: name,
          email: email,
          phone: phoneNo,
          checkin_date: check_in,
          checkout_date: check_out,
          enquired_at: enquired_at,
          existing_solene_user: existing_solene_user,
          last_modified: updated_at,
          verified: verified,
          repeat_user: repeat_user,
          poc_executive: poc_exec,
          interested_in: location,
          source,
          status: status || "no_rating",
          refer_by_tier: referred_by_tier_name,
          properties: properties,
        };

      return opportunity;
    });

    return {
      error: null,
      response: formatted,
      paginate: response.paginate,
    };
  }

  getOpportunitiesCSV(
    statuses?: string[],
    stages?: string[],
    sources?: string[],
    enquired_from?: string,
    enquired_until?: string,
    checkin_date?: string,
    checkout_date?: string,
    staff?: string,
  ): string {
    const qp = getFilterQp(
      undefined,
      undefined,
      statuses,
      stages,
      sources,
      enquired_from,
      enquired_until,
      checkin_date,
      checkout_date,
      staff,
    );

    const marshalled = qp.toString();

    let url = "/api/v2/solene/opportunities.csv";

    if (marshalled) {
      url = `${url}?${marshalled}`;
    }

    return url;
  }

  async getOpportunity(
    opportunity_slug: string,
  ): Promise<FetchResult<SoleneOpportunity>> {
    const url = `/api/v2/solene/opportunities/${opportunity_slug}`,
      { error, response } = await GET<OpportunityResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    const {
        id,
        name,
        status,
        check_in,
        check_out,
        current_stage,
        enquired_at,
        location_id,
        poc_exec_id,
        membership_exec_id,
        slug,
        stages,
        message,
        source_city,
        max_budget,
        adults_count,
        children_count,
        source,
        source_others,
        source_mobile,
        source_address,
        contact_id,
        visit_purpose,
        visit_purpose_other,
        agent_id,
        home_owner_id,
        gender,
        properties,
        reservations,
        brand_id,
        original_source,
        utm_meta,
        application_form_url,
        application_form_status,
      } = response.opportunity,
      opportunity: SoleneOpportunity = {
        opportunity_id: id,
        opportunity_slug: slug,
        enquiry_name: name || "",
        status: status || "",
        checkin_date: check_in || "",
        checkout_date: check_out || "",
        current_stage: current_stage || "",
        enquiry_date: enquired_at || "",
        location: location_id || -1,
        sales_poc_id: poc_exec_id || null,
        membership_poc_id: membership_exec_id || null,
        stages: stages || [],
        customer_id: contact_id || -1,
        message: message || "",
        utm_campaign: utm_meta?.utm_campaign || "",
        utm_medium: utm_meta?.utm_medium || "",
        utm_source: utm_meta?.utm_source || "",
        utm_content: utm_meta?.utm_content || "",
        utm_adgroup: utm_meta?.utm_adgroup || "",
        utm_term: utm_meta?.utm_term || "",
        source_city: source_city || "",
        budget: max_budget || "",
        adults: adults_count || "",
        children: children_count || "",
        source_of_enquiry: source || "",
        source_of_enquiry_phone_no: source_mobile || "",
        source_of_enquiry_address: source_address || "",
        source_of_enquiry_other: source_others || "",
        purpose_of_visit: visit_purpose || "",
        purpose_of_visit_other: visit_purpose_other || "",
        gender: gender || "",
        agent: agent_id,
        home_owner_id: home_owner_id,
        properties: properties || [],
        reservations: reservations || [],
        brand_id: brand_id || null,
        original_source: original_source || null,
        application_form_url: application_form_url || null,
        application_form_status: application_form_status || null,
      };

    return {
      error: null,
      response: opportunity,
    };
  }

  async updateOpportunity(
    opportunity_slug: string,
    message?: string,
    source_of_enquiry?: string,
    source_of_enquiry_other?: string,
  ): Promise<FetchResult<void>> {
    const template = [
        {
          key: "message",
          value: message,
        },
        {
          key: "source",
          value: source_of_enquiry,
        },
        {
          key: "source_others",
          value: source_of_enquiry_other,
        },
      ],
      payload = template.reduce((compiled, each) => {
        const { key, value } = each;
        if (value !== undefined) {
          compiled[key] = value;
        }
        return compiled;
      }, {} as { [k: string]: string | number | boolean }),
      url = `/api/v2/solene/opportunities/${opportunity_slug}`,
      { error } = await PATCH<void>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async updatePOC(
    opportunity_slug: string,
    poc_executive?: string,
    poc_head?: string,
    gr_executive?: string,
  ): Promise<FetchResult<void>> {
    const template = [
        {
          key: "poc_exec_id",
          value: poc_executive,
        },
        {
          key: "membership_exec_id",
          value: poc_head,
        },
        {
          key: "grm_exec_id",
          value: gr_executive,
        },
      ],
      payload = template.reduce((compiled, each) => {
        const { key, value } = each;
        if (value !== undefined) {
          compiled[key] = value;
        }
        return compiled;
      }, {} as { [k: string]: string | number }),
      url = `/api/v2/solene/opportunities/${opportunity_slug}/update_poc`,
      { error } = await PATCH<void>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async updateStages(
    opportunity_slug: string,
    checked: number[],
    vertical?: string,
  ): Promise<FetchResult<void>> {
    const payload = {
        checked: checked,
        vertical: vertical,
      },
      url = `/api/v2/solene/opportunities/${opportunity_slug}/stage`,
      { error } = await POST<void>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  // async checkPhone(
  //   phone_no?: string,
  //   country_code?: string,
  //   page_size?: string,
  // ): Promise<FetchResult<RentalMobileCheck[]>> {
  //   const qp = new URLSearchParams();

  //   if (country_code) {
  //     qp.set("country_code", country_code);
  //   }

  //   if (phone_no) {
  //     qp.set("mobile", phone_no);
  //   }

  //   if (page_size) {
  //     qp.set("per_page", page_size);
  //   }

  //   qp.set("show_contact", "true");

  //   const marshalled = qp.toString();

  //   let url = "/api/v2/contacts";

  //   if (marshalled) {
  //     url = `${url}?${marshalled}`;
  //   }

  //   const { error, response } = await GET<CheckPhoneResponse>(url);

  //   if (error) {
  //     return {
  //       error,
  //       paginate: undefined,
  //       response: null,
  //     };
  //   }

  //   if (!response) {
  //     return {
  //       error: new Error("Unknown error"),
  //       paginate: undefined,
  //       response: null,
  //     };
  //   }

  //   const formatted = response.contacts.map((each) => {
  //     const {
  //         id,
  //         name,
  //         primary_email_details,
  //         primary_mobile_details,
  //         customer_id,
  //         dob,
  //       } = each,
  //       nameSplits = (name || "").split(" "),
  //       customer: RentalMobileCheck = {
  //         id,
  //         first_name: nameSplits[0],
  //         last_name: nameSplits.length > 1 ? nameSplits.slice(1).join(" ") : "",
  //         email: primary_email_details,
  //         phone_no: primary_mobile_details,
  //         customer_id: customer_id,
  //         dob: dob,
  //       };
  //     return customer;
  //   });

  //   return {
  //     error: null,
  //     paginate: response.paginate,
  //     response: formatted,
  //   };
  // }

  async getOpportunityPayment(
    opportunity_slug: string,
  ): Promise<FetchResult<SoleneOpportunityPayment>> {
    const url = `/api/v2/solene/opportunities/${opportunity_slug}/payments`,
      { error, response } = await GET<PaymentResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    const formatted: (SolenePayment | SolenePaymentLink)[] =
        response.payment.map((each) => {
          const {
              id,
              amount,
              email,
              external_id,
              payment_date,
              gateway,
              country_code,
              mobile,
              name,
              status,
              // transaction_id,
              url,
              payment_for,
              currency_symbol,
              currency_id,
            } = each,
            phoneNo = [country_code || "", mobile || ""].join(" ");

          if (url) {
            const formatted: SolenePaymentLink = {
              id,
              name,
              email,
              phone_no: mobile,
              amount: amount.toLocaleString(),
              url,
              status: status,
              currency_symbol: currency_symbol || "",
              currency_id: currency_id || 1,
              payment_for: payment_for,
            };

            return formatted;
          }

          const formatted: SolenePayment = {
            id: id,
            name: name,
            email: email,
            phone_no: phoneNo,
            amount: amount || 0,
            payment_method: gateway,
            reference_no: external_id,
            payment_date: payment_date,
            payment_for: payment_for,
            status: status,
            currency_symbol: currency_symbol || "",
            currency_id: currency_id || 1,
          };

          return formatted;
        }),
      net_paid_amount = response.net_paid_amount || 0,
      total_paid_amount = response.total_paid_amount || 0,
      total_reservations_amount = response.total_reservations_amount || 0,
      total_charges_amount = response.total_charges_amount || 0,
      outstanding = response.outstanding_amount || 0,
      credit_balance = response.credits_available || 0,
      is_international = response.is_international || false,
      loyalty_points_balance = response.loyalty_points_available || 0;

    return {
      error: null,
      response: {
        net_paid_amount: net_paid_amount,
        total_paid_amount: total_paid_amount,
        total_reservations_amount: total_reservations_amount,
        total_charges_amount: total_charges_amount,
        payments: formatted,
        outstanding_amount: outstanding,
        credit_balance: credit_balance,
        is_international: is_international,
        loyalty_points_balance: loyalty_points_balance,
      },
    };
  }

  async addPayment(
    opportunity_slug: string,
    name: string,
    payment_method: string,
    currency: string,
    amount: string,
    reference_no: string,
    payment_for: string,
    payment_date?: string,
    email?: string,
    phone_no?: string,
  ): Promise<FetchResult<null>> {
    const payload: any = {
      name,
      gateway: payment_method,
      currency_id: currency,
      amount: amount,
      external_id: reference_no,
      payment_for: payment_for,
      payment_date: payment_date,
    };

    if (email !== undefined) {
      payload.email = email;
    }

    if (phone_no !== undefined) {
      const [code, phoneNo] = phone_no.split(" ");

      payload.country_code = code;

      payload.mobile = phoneNo;
    }

    const url = `/api/v2/solene/opportunities/${opportunity_slug}/payments`,
      { error, response } = await POST<null>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async updatePayment(
    opportunity_slug: string,
    payment_id: number,
    amount: string,
    reference_no: string,
    payment_method: string,
    payment_for: string,
    currency: string,
    payment_date?: string,
    name?: string,
    email?: string,
    phone_no?: string,
  ): Promise<FetchResult<void>> {
    const payload: any = {
      name,
      gateway: payment_method,
      currency_id: currency,
      amount: amount,
      external_id: reference_no,
      payment_for: payment_for,
      payment_date: payment_date,
    };

    if (email !== undefined) {
      payload.email = email;
    }

    if (phone_no !== undefined) {
      const [code, phoneNo] = phone_no.split(" ");

      payload.country_code = code;

      payload.mobile = phoneNo;
    }

    const url = `/api/v2/solene/opportunities/${opportunity_slug}/payments/${payment_id}`,
      { error, response } = await PATCH<null>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async deletePayment(
    opportunity_slug: string,
    payment_id: number,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/solene/opportunities/${opportunity_slug}/payments/${payment_id}`,
      { error } = await DELETE<null>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async removeMembership(
    opportunity_slug: string,
    profile_id: number,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/solene/opportunities/${opportunity_slug}/profiles/${profile_id}`,
      { error } = await DELETE<null>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async confirmMembership(
    opportunity_slug: string,
    profile_id: number,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/solene/opportunities/${opportunity_slug}/memberships/${profile_id}/confirm`,
      payload = {},
      { error } = await PATCH<null>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async updateMembershipStartDate(
    opportunity_slug: string,
    membership_id: number,
    start_date: string,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/solene/opportunities/${opportunity_slug}/memberships/${membership_id}`,
      payload = {
        start_date,
      },
      { error } = await PATCH<null>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async approvePayment(
    opportunity_slug: string,
    payment_id: number,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/solene/opportunities/${opportunity_slug}/payments/${payment_id}`,
      payload = {
        approved: true,
      },
      { error } = await PATCH<null>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async checkPaymentStatus(
    opportunity_slug: string,
    payment_id: number,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/solene/opportunities/${opportunity_slug}/payments/${payment_id}/status`,
      { error } = await POST<null>(url, {});

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async getApprovalRequests(
    status?: string,
    from_date?: string,
    until_date?: string,
    search?: string,
    page?: string,
    page_size?: string,
  ): Promise<FetchResult<SolenePaymentApprovalItem[]>> {
    const qp = new URLSearchParams();

    if (search) {
      qp.set("query", search);
    }

    if (from_date) {
      qp.set("start_date", from_date);
    }

    if (until_date) {
      qp.set("end_date", until_date);
    }

    if (status) {
      qp.set("approval_status", status);
    }

    if (page) {
      qp.set("page", page);
    }

    if (page_size) {
      qp.set("per_page", page_size);
    }

    const marshalled = qp.toString();

    let url = "/api/v2/solene/payments";

    if (marshalled) {
      url = `${url}?${marshalled}`;
    }

    const { error, response } = await GET<SoleneApprovalsListResponse>(url);

    if (error) {
      return {
        error: null,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown Error"),
        response: null,
      };
    }

    const properties = response.payments.map((each) => {
      const {
          id,
          guest_name,
          amount,
          payment_date,
          payment_for,
          approval_status,
          opportunity_slug,
          propertie_names,
          poc_exec,
          reference_no,
          created_at,
          gateway,
        } = each,
        nameSplits = (guest_name || "").split(" "),
        property: SolenePaymentApprovalItem = {
          id: id,
          opportunity_slug: opportunity_slug || "",
          first_name: nameSplits[0] || "",
          last_name: nameSplits.length > 1 ? nameSplits.slice(1).join(" ") : "",
          property_name: propertie_names || "",
          poc_name: poc_exec || "",
          amount: amount ?? 0,
          payment_for: payment_for ?? "",
          payment_method: gateway ?? "",
          reference_no: reference_no ?? "",
          requested_date: created_at || "",
          payment_date: payment_date || "",
          status: approval_status || "",
        };

      return property;
    });

    return {
      error: null,
      paginate: response.paginate,
      response: properties,
    };
  }

  async approveRequest(
    opportunitySlug: string,
    id: number,
    reason: string,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/solene/opportunities/${opportunitySlug}/payments/${id}/approve`,
      { error } = await PATCH<SoleneApprovalResponse>(url, {
        comments: reason,
      });

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async questionRequest(
    opportunitySlug: string,
    id: number,
    reason: string,
    comments: string,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/solene/opportunities/${opportunitySlug}/payments/${id}/reject`,
      payload = { reason: reason, comments: comments },
      { error } = await PATCH<SoleneApprovalResponse>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async addMemberProfile(
    opp_slug: string,
    membership_type: string,
    name: string,
    email: string,
    phone_no: string,
    date_of_birth: string,
    address: string,
    profile_id?: number,
    membership_number?: string,
    relationship?: string,
    social_media?: string,
    // profession?: string,
    // company?: string,
    villa_location?: string,
    likes?: string,
    dislikes?: string,
    notes?: string,
    home?: string,
    anniversary?: string,
  ): Promise<FetchResult<SoleneOpportunityMemberProfile>> {
    const [countryCode, phoneNo] = phone_no.split(" ");

    const payload: any = {
      profile_type: membership_type || "",
      name: name || "",
      email: email || "",
      country_code: countryCode || "",
      mobile: phoneNo || "",
      date_of_birth: date_of_birth || "",
      address: address || "",
      membership_number: membership_number || "",
      relationship: relationship || "",
      social_media_handle: social_media || "",
      // profession: profession || "",
      // company: company || "",
      villa_location: villa_location || "",
      likes: likes || "",
      dislikes: dislikes || "",
      notes: notes || "",
      home: home || "",
      anniversary: anniversary || "",
    };

    if (profile_id) {
      payload.id = profile_id;
    }

    const url = `/api/v2/solene/opportunities/${opp_slug}/profiles`,
      { response, error } = await POST<MemberProfileAddResponse>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        paginate: undefined,
        response: null,
      };
    }

    const { profile } = response,
      formatted: SoleneOpportunityMemberProfile = {
        actions: profile.actions,
        profile_id: profile.id,
        name: profile.name,
        profile_type: profile.profile_type,
        membership_id: profile.membership.membership_id,
        membership_plan_id: profile.membership.membership_plan_id,
        amount: profile.membership.amount,
        currency: profile.membership.currency,
        status: profile.membership.status,
        membership_number: profile.membership.membership_number,
        membership_type: profile.membership.membership_type,
        start_date: profile.membership.start_date,
        end_date: profile.membership.end_date,
        breakdown: profile.membership.breakdown,
        email: profile.membership.email,
        phone_no: profile.membership.mobile
          ? `${profile.membership.country_code} ${profile.membership.mobile}`
          : "",
        address: profile.address,
        anniversary: profile.anniversary,
        // company: profile.company,
        date_of_birth: profile.date_of_birth,
        villa_location: profile.villa_location,
        likes: profile.likes,
        dislikes: profile.dislikes,
        notes: profile.notes,
        // profession: profile.profession,
        relationship: profile.relationship,
        social_media: profile.social_media_handle,
        home: profile.home,
      };

    return {
      error: null,
      response: formatted,
    };
  }

  async updateMemberProfile(
    opp_slug: string,
    profile_id: number,
    membership_type: string,
    name: string,
    email: string,
    phone_no: string,
    date_of_birth: string,
    address: string,
    membership_number?: string,
    relationship?: string,
    social_media?: string,
    // profession?: string,
    // company?: string,
    villa_location?: string,
    likes?: string,
    dislikes?: string,
    notes?: string,
    home?: string,
    anniversary?: string,
  ): Promise<FetchResult<SoleneOpportunityMemberProfile>> {
    const [countryCode, phoneNo] = phone_no.split(" ");

    const payload: any = {
      profile_type: membership_type || "",
      name: name || "",
      email: email || "",
      country_code: countryCode || "",
      mobile: phoneNo || "",
      date_of_birth: date_of_birth || "",
      address: address || "",
      membership_number: membership_number || "",
      relationship: relationship || "",
      social_media_handle: social_media || "",
      // profession: profession || "",
      // company: company || "",
      villa_location: villa_location || "",
      likes: likes || "",
      dislikes: dislikes || "",
      notes: notes || "",
      home: home || "",
      anniversary: anniversary || "",
    };

    const url = `/api/v2/solene/opportunities/${opp_slug}/profiles/${profile_id}`,
      { response, error } = await PATCH<MemberProfileAddResponse>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        paginate: undefined,
        response: null,
      };
    }

    const { profile } = response,
      formatted: SoleneOpportunityMemberProfile = {
        actions: profile.actions,
        profile_id: profile.id,
        name: profile.name,
        profile_type: profile.profile_type,
        membership_id: profile.membership.membership_id,
        membership_plan_id: profile.membership.membership_plan_id,
        amount: profile.membership.amount,
        currency: profile.membership.currency,
        status: profile.membership.status,
        membership_number: profile.membership.membership_number,
        membership_type: profile.membership.membership_type,
        start_date: profile.membership.start_date,
        end_date: profile.membership.end_date,
        breakdown: profile.membership.breakdown,
        email: profile.membership.email,
        phone_no: profile.membership.mobile
          ? `${profile.membership.country_code} ${profile.membership.mobile}`
          : "",
        address: profile.address,
        anniversary: profile.anniversary,
        // company: profile.company,
        date_of_birth: profile.date_of_birth,
        villa_location: profile.villa_location,
        likes: profile.likes,
        dislikes: profile.dislikes,
        notes: profile.notes,
        // profession: profile.profession,
        relationship: profile.relationship,
        social_media: profile.social_media_handle,
        home: profile.home,
      };

    return {
      error: null,
      response: formatted,
    };
  }

  async getMembersList(
    type?: string[],
    category?: string[],
    start_date?: string,
    end_date?: string,
    search?: string,
    page?: string,
    page_size?: string,
  ): Promise<FetchResult<SoleneMemberItem[]>> {
    const qp = getMembersListQp(
      type,
      category,
      start_date,
      end_date,
      search,
      page,
      page_size,
    );

    const marshalled = qp.toString();

    let url = "/api/v2/solene/memberships/list?status=active";

    if (marshalled) {
      url = `${url}&${marshalled}`;
    }

    const { error, response } = await GET<MembershipsListResponse>(url);

    if (error) {
      return {
        error,
        paginate: undefined,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        paginate: undefined,
        response: null,
      };
    }

    const formatted = response.memberships.map((each) => {
      const {
          id,
          profile_name,
          mobile,
          country_code,
          profile_type,
          membership_plan_type,
          start_date,
          end_date,
          home,
          opportunity_slug,
          membership_number,
          status,
        } = each,
        phoneNo = `${country_code || ""} ${mobile || ""}`.trim(),
        opportunity: SoleneMemberItem = {
          profile_id: id,
          slug: opportunity_slug,
          name: profile_name,
          phone: phoneNo,
          type: profile_type,
          category: membership_plan_type,
          start_date: start_date,
          end_date: end_date,
          home: home,
          membership_number: membership_number,
          status: status,
        };

      return opportunity;
    });

    return {
      error: null,
      response: formatted,
      paginate: response.paginate,
    };
  }

  async getMembersDetails(
    slug: string,
  ): Promise<FetchResult<SoleneOpportunityMembersData>> {
    const url = `/api/v2/solene/opportunities/${slug}/profiles`,
      { error, response } = await GET<MembersProfileListResponse>(url);

    if (error) {
      return {
        error,
        paginate: undefined,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        paginate: undefined,
        response: null,
      };
    }

    const formatted = response.profiles.map((each) => {
      const {
          actions,
          id,
          membership,
          name,
          profile_type,
          address,
          anniversary,
          // company,
          date_of_birth,
          notes,
          // profession,
          relationship,
          social_media_handle,
          home,
        } = each,
        opportunity: SoleneOpportunityMemberProfile = {
          actions: actions,
          profile_id: id,
          name: name,
          profile_type: profile_type,
          membership_id: membership.membership_id,
          membership_plan_id: membership.membership_plan_id,
          amount: membership.amount,
          currency: membership.currency,
          status: membership.status,
          membership_number: membership.membership_number,
          membership_type: membership.membership_type,
          start_date: membership.start_date,
          end_date: membership.end_date,
          breakdown: membership.breakdown,
          email: membership.email,
          phone_no: membership.mobile
            ? `${membership.country_code} ${membership.mobile}`
            : "",
          address: address,
          anniversary: anniversary,
          // company: company,
          date_of_birth: date_of_birth,
          villa_location: each.villa_location,
          likes: each.likes,
          dislikes: each.dislikes,
          notes: notes,
          // profession: profession,
          relationship: relationship,
          social_media: social_media_handle,
          home: home,
          membership_plan_display_name: membership.membership_plan_display_name,
        };

      return opportunity;
    });

    return {
      error: null,
      response: {
        profiles: formatted,
        total_amount: response.total_amount,
      },
    };
  }

  async getMembersProfileDetails(
    slug: string,
    profile_id: number,
  ): Promise<FetchResult<SoleneOpportunityMemberProfile>> {
    const url = `/api/v2/solene/opportunities/${slug}/memberships/${profile_id}`,
      { error, response } = await GET<MemberProfileGetResponse>(url);

    if (error) {
      return {
        error,
        paginate: undefined,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        paginate: undefined,
        response: null,
      };
    }

    const { membership } = response,
      formatted: SoleneOpportunityMemberProfile = {
        actions: membership.actions,
        profile_id: membership.id,
        name: membership.name,
        profile_type: membership.profile_type,
        membership_id: membership.membership.membership_id,
        membership_plan_id: membership.membership.membership_plan_id,
        amount: membership.membership.amount,
        currency: membership.membership.currency,
        status: membership.membership.status,
        membership_number: membership.membership.membership_number,
        membership_type: membership.membership.membership_type,
        start_date: membership.membership.start_date,
        end_date: membership.membership.end_date,
        breakdown: membership.membership.breakdown,
        email: membership.membership.email,
        phone_no: membership.membership.mobile
          ? `${membership.membership.country_code} ${membership.membership.mobile}`
          : "",
        address: membership.address,
        anniversary: membership.anniversary,
        // company: membership.company,
        date_of_birth: membership.date_of_birth,
        villa_location: membership.villa_location,
        likes: membership.likes,
        dislikes: membership.dislikes,
        notes: membership.notes,
        // profession: membership.profession,
        relationship: membership.relationship,
        social_media: membership.social_media_handle,
        home: membership.home,
      };

    return {
      error: null,
      response: formatted,
    };
  }

  async getMembershipPricing(
    membership_type: string,
  ): Promise<FetchResult<SoleneOpportunityMembership>> {
    const url = `/api/v2/solene/membership_plans/${membership_type}`,
      { error, response } = await GET<MembershipResponse>(url);

    if (error) {
      return {
        error,
        paginate: undefined,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        paginate: undefined,
        response: null,
      };
    }

    const { base_amount, id, gst_percentage } = response.membership_plan,
      formatted: SoleneOpportunityMembership = {
        membership_plan_id: id,
        gst_percentage: gst_percentage,
        list_rate: base_amount,
      };

    return {
      error: null,
      response: formatted,
    };
  }

  async addMemberShip(
    opp_slug: string,
    membership_id: number,
    profile_id: number,
    membership_type: string,
    flat_discount: number,
    ivpl_budget: number,
  ): Promise<FetchResult<void>> {
    const payload: any = {
      membership_id: membership_id,
      membership_profile_id: profile_id,
      membership_plan_id: Number(membership_type),
      discount: flat_discount || 0,
      ivpl_budget: ivpl_budget || 0,
    };

    const url = `/api/v2/solene/opportunities/${opp_slug}/memberships`,
      { error } = await POST<MemberProfileAddResponse>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  //Attachments
  async getSoleneDocuments(
    opportunity_slug: string,
    document_type?: string,
  ): Promise<FetchResult<SoleneDocument[]>> {
    let url = `/api/v2/solene/opportunities/${opportunity_slug}/documents`;
    const qp = new URLSearchParams();

    if (document_type) {
      qp.set("document_type", document_type);
    }
    const marshaled = qp.toString();
    if (marshaled) {
      url = `${url}?${marshaled}`;
    }

    const { error, response } = await GET<SoleneDocumentsResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    const formatted: SoleneDocument[] = response.documents.map((each) => {
      const { id, link, document_name, document_identifier, document_type } =
          each,
        formatted: SoleneDocument = {
          id: id,
          type: document_type || "",
          name: document_name || document_identifier || "",
          file: link || "",
        };

      return formatted;
    });

    return {
      error: null,
      response: formatted,
    };
  }

  async getClubVisitLookups(): Promise<FetchResult<SoleneClubVisitLookups>> {
    const { error, response } = await GET<SoleneClubVisitLookups>(
      "/api/v2/solene/club_visits/lookups",
    );

    if (error) {
      return { error, response: null };
    }

    if (!response) {
      return { error: new Error("Unknown error"), response: null };
    }

    return { error: null, response };
  }

  async getClubVisits(
    search?: string,
    record_type?: string,
    sources?: string[],
    from_date?: string,
    to_date?: string,
    page?: string,
    page_size = "10",
  ): Promise<FetchResult<SoleneClubVisitListingRow[]>> {
    const qp = new URLSearchParams();

    if (search) {
      qp.set("query", search);
    }
    if (record_type && record_type !== "all") {
      qp.set("record_type", record_type);
    }
    if (sources) {
      sources.forEach((source) => qp.append("source[]", source));
    }
    if (from_date) {
      qp.set("from_date", from_date);
    }
    if (to_date) {
      qp.set("to_date", to_date);
    }
    qp.set("page", page || "1");
    qp.set("per_page", page_size);

    const url = `/api/v2/solene/club_visits?${qp.toString()}`,
      { error, response } = await GET<ClubVisitsListResponse>(url);

    if (error) {
      return { error, response: null };
    }

    if (!response) {
      return { error: new Error("Unknown error"), response: null };
    }

    return {
      error: null,
      paginate: response.paginate,
      response: response.club_visits,
    };
  }

  async getClubVisitMemberSearch(
    query?: string,
  ): Promise<FetchResult<SoleneClubVisitMember[]>> {
    const qp = new URLSearchParams();

    if (query) {
      qp.set("query", query);
    }

    const queryString = qp.toString(),
      url = `/api/v2/solene/club_visits/member_search${
        queryString ? `?${queryString}` : ""
      }`,
      { error, response } = await GET<MemberSearchResponse>(url);

    if (error) {
      return { error, response: null };
    }

    if (!response) {
      return { error: new Error("Unknown error"), response: null };
    }

    return { error: null, response: response.members };
  }

  async getClubVisit(id: number): Promise<FetchResult<SoleneClubVisitDetails>> {
    const { error, response } = await GET<ClubVisitResponse>(
      `/api/v2/solene/club_visits/${id}`,
    );

    if (error) {
      return { error, response: null };
    }

    if (!response) {
      return { error: new Error("Unknown error"), response: null };
    }

    return { error: null, response: response.club_visit };
  }

  async createClubVisit(
    payload: SoleneClubVisitFormPayload,
  ): Promise<FetchResult<SoleneClubVisitDetails>> {
    const { error, response } = await POST<ClubVisitResponse>(
      "/api/v2/solene/club_visits",
      payload,
    );

    if (error) {
      return { error, response: null };
    }

    if (!response) {
      return { error: new Error("Unknown error"), response: null };
    }

    return { error: null, response: response.club_visit };
  }

  async updateClubVisit(
    id: number,
    payload: SoleneClubVisitFormPayload,
  ): Promise<FetchResult<SoleneClubVisitDetails>> {
    const { error, response } = await PATCH<ClubVisitResponse>(
      `/api/v2/solene/club_visits/${id}`,
      payload,
    );

    if (error) {
      return { error, response: null };
    }

    if (!response) {
      return { error: new Error("Unknown error"), response: null };
    }

    return { error: null, response: response.club_visit };
  }

  async getLohonoCheck(
    mobile: string,
    visit_date: string,
  ): Promise<FetchResult<SoleneLohonoCheckResult>> {
    const qp = new URLSearchParams({ mobile, visit_date }),
      { error, response } = await GET<LohonoCheckResponse>(
        `/api/v2/solene/club_visits/lohono_check?${qp.toString()}`,
      );

    if (error) {
      return { error, response: null };
    }

    if (!response) {
      return { error: new Error("Unknown error"), response: null };
    }

    return {
      error: null,
      response: response.lohono_check || response,
    };
  }

  async getDayPassLineItem(
    id: number,
  ): Promise<FetchResult<SoleneDayPassDetails>> {
    const { error, response } = await GET<DayPassLineItemResponse>(
      `/api/v2/solene/day_passes/line_items/${id}`,
    );

    if (error) {
      return { error, response: null };
    }

    if (!response) {
      return { error: new Error("Unknown error"), response: null };
    }

    const dayPass = response.day_pass_line_item || response.day_pass;

    if (!dayPass) {
      return { error: new Error("Unknown error"), response: null };
    }

    return {
      error: null,
      response: normaliseDayPassLineItem(dayPass),
    };
  }

  async createDayPass(
    payload: SoleneDayPassFormPayload,
  ): Promise<FetchResult<SoleneDayPassTransactionDetails>> {
    const { error, response } = await POST<DayPassResponse>(
      "/api/v2/solene/day_passes",
      payload,
    );

    if (error) {
      return { error, response: null };
    }

    if (!response) {
      return { error: new Error("Unknown error"), response: null };
    }

    const dayPass = response.day_pass_transaction || response.day_pass;

    if (!dayPass) {
      return { error: new Error("Unknown error"), response: null };
    }

    return {
      error: null,
      response: dayPass,
    };
  }

  async updateDayPassLineItem(
    id: number,
    payload: SoleneDayPassLineItemPayload & { payment_collected?: boolean },
  ): Promise<FetchResult<SoleneDayPassDetails>> {
    const { error, response } = await PATCH<DayPassLineItemResponse>(
      `/api/v2/solene/day_passes/line_items/${id}`,
      payload,
    );

    if (error) {
      return { error, response: null };
    }

    if (!response) {
      return { error: new Error("Unknown error"), response: null };
    }

    const dayPass = response.day_pass_line_item || response.day_pass;

    if (!dayPass) {
      return { error: new Error("Unknown error"), response: null };
    }

    return {
      error: null,
      response: dayPass,
    };
  }

  async addSoleneDocument(
    opportunity_slug: string,
    document_type: string,
    document_name: string,
    file: File,
  ): Promise<FetchResult<void>> {
    const formData = new FormData();
    formData.set("document_type", document_type);
    formData.set("document_name", document_name);
    formData.set("file", file);

    const url = `/api/v2/solene/opportunities/${opportunity_slug}/documents`,
      { error } = await POST<void>(url, formData);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async deleteSoleneDocument(
    opportunity_slug: string,
    document_id: number,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/solene/opportunities/${opportunity_slug}/documents/${document_id}`,
      { error } = await DELETE<void>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }
}

export default SoleneService;

function getFilterQp(
  vertical?: string,
  phone_no?: string,
  statuses?: string[],
  stages?: string[],
  sources?: string[],
  enquired_from?: string,
  enquired_until?: string,
  checkin_date?: string,
  checkout_date?: string,
  staff?: string,
  location_ids?: string[],
  search?: string,
  page?: string,
  page_size?: string,
) {
  const qp = new URLSearchParams();
  if (vertical) {
    qp.set("vertical", vertical);
  }
  if (phone_no) {
    qp.set("mobile", phone_no);
  }
  if (statuses) {
    statuses.forEach((status) => {
      qp.append("status[]", status);
    });
  }
  if (stages) {
    stages.forEach((status) => {
      qp.append("stages[]", status);
    });
  }
  if (sources) {
    sources.forEach((source) => {
      qp.append("sources[]", source);
    });
  }
  if (location_ids) {
    location_ids.forEach((locations) => {
      qp.append("location_ids[]", locations);
    });
  }
  if (search) {
    qp.set("query", search);
  }
  if (enquired_from) {
    qp.set("start_date", enquired_from);
  }
  if (enquired_until) {
    qp.set("end_date", enquired_until);
  }
  if (checkin_date) {
    qp.set("check_in", checkin_date);
  }
  if (checkout_date) {
    qp.set("check_out", checkout_date);
  }
  if (staff) {
    qp.set("staff_id", staff);
  }
  if (page) {
    qp.set("page", page);
  }
  if (page_size) {
    qp.set("per_page", page_size);
  }

  return qp;
}

type OpportunityAddResponse = {
  slug: string;
};

type OpportunitiesListResponse = {
  opportunities: any[];
  paginate: Pagination;
};

type MembershipsListResponse = {
  memberships: any[];
  paginate: Pagination;
};

type OpportunityResponse = {
  opportunity: any;
};

type PaymentResponse = {
  payment: any[];
  outstanding_amount: number;
  credits_available: number;
  net_paid_amount: number;
  total_paid_amount: number;
  total_reservations_amount: number;
  total_charges_amount: number;
  is_international: boolean;
  loyalty_points_available: number;
};

type SoleneApprovalsListResponse = {
  payments: any[];
  paginate: Pagination;
};

type SoleneApprovalResponse = {
  property: any;
};

type MemberProfileAddResponse = {
  profile: any;
};

type MemberProfileGetResponse = {
  membership: any;
};

type MembersProfileListResponse = {
  profiles: any[];
  total_amount: number;
};

type MembershipResponse = {
  membership_plan: any;
};

type SoleneDocumentsResponse = {
  documents: any[];
};

type ClubVisitsListResponse = {
  club_visits: SoleneClubVisitListingRow[];
  paginate: Pagination;
  meta?: Record<string, unknown>;
};

type MemberSearchResponse = {
  members: SoleneClubVisitMember[];
};

type ClubVisitResponse = {
  club_visit: SoleneClubVisitDetails;
};

type LohonoCheckResponse = SoleneLohonoCheckResult & {
  lohono_check?: SoleneLohonoCheckResult;
};

type DayPassLineItemResponse = {
  day_pass_line_item?: SoleneDayPassDetails;
  day_pass?: SoleneDayPassDetails;
};

type DayPassResponse = DayPassLineItemResponse & {
  day_pass?: SoleneDayPassTransactionDetails;
  day_pass_transaction?: SoleneDayPassTransactionDetails;
};

function normaliseDayPassLineItem(
  payload: SoleneDayPassDetails | any,
): SoleneDayPassDetails {
  if (payload.line_item && !payload.day_pass_transaction) {
    return payload;
  }

  const parent = payload.day_pass_transaction || {},
    member = parent.membership || null,
    lineItem: SoleneDayPassLineItem = {
      id: payload.id,
      visitor_name: payload.visitor_name,
      mobile: payload.mobile,
      facilities: payload.facilities || [],
      facility_snapshots: payload.facility_snapshots || [],
      base_amount: payload.base_amount || 0,
      to_collect: payload.to_collect || 0,
      note: payload.note,
      payment_collected: Boolean(payload.payment_collected),
      payment_collected_at: payload.payment_collected_at || null,
      payment_collector: payload.payment_collector || null,
      collection_status:
        payload.collection_status ||
        (payload.to_collect === 0
          ? "complimentary"
          : payload.payment_collected
          ? "collected"
          : "pending"),
      locked: Boolean(payload.locked),
    };

  return {
    id: parent.id || payload.id,
    transaction_date: parent.transaction_date,
    transaction_time: parent.transaction_time,
    source: parent.source,
    source_label_snapshot: parent.source_label_snapshot,
    source_other_text: parent.source_other_text || null,
    member,
    membership_plan_snapshot: parent.membership_plan_snapshot || null,
    lohono_mobile: parent.lohono_mobile || null,
    lohono_reservation_snapshots: parent.lohono_reservation_snapshots || [],
    discount_percent: Number(parent.discount_percent || 0),
    notes: parent.notes || null,
    line_item: lineItem,
    locked: Boolean(payload.locked),
  };
}

function getMembersListQp(
  type?: string[],
  category?: string[],
  start_date?: string,
  end_date?: string,
  search?: string,
  page?: string,
  page_size?: string,
) {
  const qp = new URLSearchParams();
  if (type) {
    type.forEach((type) => {
      qp.append("membership_plan_ids[]", type);
    });
  }
  if (category) {
    category.forEach((category) => {
      qp.append("profile_types[]", category);
    });
  }
  if (start_date) {
    qp.set("start_date", start_date);
  }
  if (end_date) {
    qp.set("end_date", end_date);
  }
  if (search) {
    qp.set("query", search);
  }
  if (page) {
    qp.set("page", page);
  }
  if (page_size) {
    qp.set("per_page", page_size);
  }

  return qp;
}
